import React from 'react';
import { 
  PlusIcon,
  ChartBarIcon,
  ListBulletIcon,
  ArrowDownTrayIcon,
  FolderIcon,
  Squares2X2Icon,
  ArchiveBoxIcon,
  CheckCircleIcon,
  ArrowPathIcon,
  EllipsisVerticalIcon,
  FunnelIcon,
  Bars3BottomRightIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CheckBadgeIcon,
  QuestionMarkCircleIcon
} from '@heroicons/react/24/outline';
import { RocketLaunchIcon, BoltIcon } from '@heroicons/react/24/solid';

export default function MyLists() {
  return (
    <div className="h-full flex flex-col relative">
      <div className="flex-1 px-10 py-12 overflow-y-auto">
        <div className="max-w-[1200px] mx-auto flex flex-col gap-8">
          
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-2">
            <div>
              <h1 className="text-[32px] font-extrabold text-slate-900 tracking-tight">My Lists Repository</h1>
              <p className="text-[16px] text-slate-500 mt-2 font-medium max-w-xl leading-relaxed">
                Manage your curated lead datasets, monitor export progress, and access high-precision intelligence.
              </p>
            </div>
            <div>
              <button className="flex items-center gap-2 bg-brand-500 text-white px-6 py-3 rounded-[14px] text-[15px] font-bold hover:bg-brand-600 transition-colors shadow-lg shadow-brand-500/25">
                <PlusIcon className="w-5 h-5" /> New List
              </button>
            </div>
          </div>

          {/* Metrics Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Metric 1 */}
            <div className="bg-white border border-slate-200 rounded-[24px] p-8 flex items-center justify-between shadow-sm hover:shadow-md transition-shadow">
              <div>
                <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">Decision Makers Found</p>
                <h3 className="text-[32px] font-extrabold text-slate-900 tracking-tight">128,402</h3>
              </div>
              <div className="w-14 h-14 rounded-2xl bg-brand-50 border border-brand-100 flex items-center justify-center shrink-0">
                <ChartBarIcon className="w-7 h-7 text-brand-600" />
              </div>
            </div>

            {/* Metric 2 */}
            <div className="bg-white border border-slate-200 rounded-[24px] p-8 flex items-center justify-between shadow-sm hover:shadow-md transition-shadow">
              <div>
                <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">Currently Running Lists</p>
                <h3 className="text-[32px] font-extrabold text-slate-900 tracking-tight">24</h3>
              </div>
              <div className="w-14 h-14 rounded-2xl bg-purple-50 border border-purple-100 flex items-center justify-center shrink-0">
                <ListBulletIcon className="w-7 h-7 text-purple-600" />
              </div>
            </div>

            {/* Metric 3 */}
            <div className="bg-white border border-slate-200 rounded-[24px] p-8 flex items-center justify-between shadow-sm hover:shadow-md transition-shadow">
              <div>
                <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">Ready For Export</p>
                <h3 className="text-[32px] font-extrabold text-slate-900 tracking-tight">19</h3>
              </div>
              <div className="w-14 h-14 rounded-2xl bg-slate-100 border border-slate-200 flex items-center justify-center shrink-0">
                <ArrowDownTrayIcon className="w-7 h-7 text-slate-600" />
              </div>
            </div>
          </div>

          {/* Table Section */}
          <div className="bg-white border border-slate-200 rounded-[24px] shadow-sm flex flex-col overflow-hidden">
            
            {/* Table Toolbar */}
            <div className="px-8 py-6 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <h2 className="text-[18px] font-extrabold text-slate-900">All Searches</h2>
                <span className="bg-slate-100 text-slate-600 px-3 py-1 rounded-full text-[12px] font-bold border border-slate-200/60">
                  All Items
                </span>
              </div>
              <div className="flex items-center gap-3">
                <button className="w-9 h-9 flex items-center justify-center text-slate-400 hover:text-slate-900 hover:bg-slate-50 rounded-full transition-colors">
                  <Bars3BottomRightIcon className="w-5 h-5" />
                </button>
                <button className="w-9 h-9 flex items-center justify-center text-slate-400 hover:text-slate-900 hover:bg-slate-50 rounded-full transition-colors">
                  <FunnelIcon className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Table Body */}
            <div className="overflow-x-auto">
              <table className="w-full text-left whitespace-nowrap">
                <thead>
                  <tr className="bg-slate-50/50 text-[11px] font-bold text-slate-400 uppercase tracking-widest border-b border-slate-100">
                    <th className="px-8 py-4">List Name</th>
                    <th className="px-8 py-4">Date & Timecreated</th>
                    <th className="px-8 py-4">Lead Count</th>
                    <th className="px-8 py-4">Export Status</th>
                    <th className="px-8 py-4 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  
                  {/* Row 1 */}
                  <tr className="hover:bg-slate-50 transition-colors">
                    <td className="px-8 py-6">
                      <div className="flex items-center gap-5">
                        <div className="w-12 h-12 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center shrink-0">
                          <FolderIcon className="w-6 h-6 text-brand-500" />
                        </div>
                        <div>
                          <h4 className="text-[15px] font-bold text-slate-900 mb-0.5">Fortune 500 SaaS Leaders</h4>
                          <p className="text-[12px] text-slate-500 font-medium">Rows : 1000, Columns : 4</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-8 py-6 text-[13px] text-slate-600 font-medium">
                      Oct 24, 2023 - 9:30PM EST
                    </td>
                    <td className="px-8 py-6 text-[15px] font-extrabold text-slate-900">
                      12,450
                    </td>
                    <td className="px-8 py-6">
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-brand-50 border border-brand-100 text-brand-600 text-[12px] font-bold rounded-full">
                        <CheckCircleIcon className="w-4 h-4" /> Ready
                      </span>
                    </td>
                    <td className="px-8 py-6 text-center">
                      <button className="text-slate-400 hover:text-slate-900 transition-colors">
                        <EllipsisVerticalIcon className="w-6 h-6 mx-auto" />
                      </button>
                    </td>
                  </tr>

                  {/* Row 2 */}
                  <tr className="hover:bg-slate-50 transition-colors">
                    <td className="px-8 py-6">
                      <div className="flex items-center gap-5">
                        <div className="w-12 h-12 rounded-xl bg-slate-100 border border-slate-200/60 flex items-center justify-center shrink-0">
                          <Squares2X2Icon className="w-6 h-6 text-slate-500" />
                        </div>
                        <div>
                          <h4 className="text-[15px] font-bold text-slate-900 mb-0.5">Fintech Founders Q4</h4>
                          <p className="text-[12px] text-slate-500 font-medium">EMEA • Seed/Series A</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-8 py-6 text-[13px] text-slate-600 font-medium">
                      Nov 02, 2023
                    </td>
                    <td className="px-8 py-6 text-[15px] font-extrabold text-slate-900">
                      4,209
                    </td>
                    <td className="px-8 py-6">
                      <div className="flex flex-col items-start gap-1.5">
                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-200/50 border border-slate-200 text-slate-600 text-[12px] font-bold rounded-full">
                          <ArrowPathIcon className="w-4 h-4 animate-spin" /> Processing
                        </span>
                        <span className="text-[11px] font-bold text-slate-500 uppercase tracking-widest pl-2">ETA : 13 Min</span>
                      </div>
                    </td>
                    <td className="px-8 py-6 text-center">
                      <button className="text-slate-400 hover:text-slate-900 transition-colors">
                        <EllipsisVerticalIcon className="w-6 h-6 mx-auto" />
                      </button>
                    </td>
                  </tr>

                  {/* Row 3 */}
                  <tr className="hover:bg-slate-50 transition-colors">
                    <td className="px-8 py-6">
                      <div className="flex items-center gap-5">
                        <div className="w-12 h-12 rounded-xl bg-red-50 border border-red-100 flex items-center justify-center shrink-0">
                          <RocketLaunchIcon className="w-6 h-6 text-red-500" />
                        </div>
                        <div>
                          <h4 className="text-[15px] font-bold text-slate-900 mb-0.5">Y-Combinator Alumni 2023</h4>
                          <p className="text-[12px] text-slate-500 font-medium">Global • Stealth Mode</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-8 py-6 text-[13px] text-slate-600 font-medium">
                      Nov 05, 2023
                    </td>
                    <td className="px-8 py-6 text-[15px] font-extrabold text-slate-900">
                      890
                    </td>
                    <td className="px-8 py-6">
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-brand-50 border border-brand-100 text-brand-600 text-[12px] font-bold rounded-full">
                        <CheckCircleIcon className="w-4 h-4" /> Ready
                      </span>
                    </td>
                    <td className="px-8 py-6 text-center">
                      <button className="text-slate-400 hover:text-slate-900 transition-colors">
                        <EllipsisVerticalIcon className="w-6 h-6 mx-auto" />
                      </button>
                    </td>
                  </tr>

                  {/* Row 4 */}
                  <tr className="hover:bg-slate-50 transition-colors">
                    <td className="px-8 py-6">
                      <div className="flex items-center gap-5">
                        <div className="w-12 h-12 rounded-xl bg-slate-100 border border-slate-200/60 flex items-center justify-center shrink-0">
                          <ArchiveBoxIcon className="w-6 h-6 text-slate-500" />
                        </div>
                        <div>
                          <h4 className="text-[15px] font-bold text-slate-900 mb-0.5">Cloud Infrastructure VP's</h4>
                          <p className="text-[12px] text-slate-500 font-medium">Enterprise • Top 100 Firms</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-8 py-6 text-[13px] text-slate-600 font-medium">
                      Nov 12, 2023
                    </td>
                    <td className="px-8 py-6 text-[15px] font-extrabold text-slate-900">
                      2,150
                    </td>
                    <td className="px-8 py-6">
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-brand-50 border border-brand-100 text-brand-600 text-[12px] font-bold rounded-full">
                        <CheckCircleIcon className="w-4 h-4" /> Ready
                      </span>
                    </td>
                    <td className="px-8 py-6 text-center">
                      <button className="text-slate-400 hover:text-slate-900 transition-colors">
                        <EllipsisVerticalIcon className="w-6 h-6 mx-auto" />
                      </button>
                    </td>
                  </tr>

                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="px-8 py-5 border-t border-slate-100 flex items-center justify-between bg-white">
              <span className="text-[13px] font-medium text-slate-500">
                Showing 1 to 4 of 24 lists
              </span>
              <div className="flex items-center gap-2">
                <button className="w-9 h-9 rounded-lg border border-slate-200 flex items-center justify-center text-slate-400 hover:bg-slate-50 transition-colors">
                  <ChevronLeftIcon className="w-4 h-4" />
                </button>
                <button className="w-9 h-9 rounded-lg bg-brand-500 text-white font-bold flex items-center justify-center shadow-md shadow-brand-500/20">
                  1
                </button>
                <button className="w-9 h-9 rounded-lg border border-slate-200 flex items-center justify-center text-slate-600 hover:bg-slate-50 font-bold transition-colors">
                  2
                </button>
                <button className="w-9 h-9 rounded-lg border border-slate-200 flex items-center justify-center text-slate-600 hover:bg-slate-50 font-bold transition-colors">
                  3
                </button>
                <button className="w-9 h-9 rounded-lg border border-slate-200 flex items-center justify-center text-slate-600 hover:bg-slate-50 transition-colors">
                  <ChevronRightIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Bottom Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-6">
            
            {/* API Card */}
            <div className="bg-[#0f172a] rounded-[32px] p-10 text-white relative overflow-hidden shadow-xl shadow-slate-900/10">
              <div className="relative z-10">
                <h3 className="text-[24px] font-extrabold mb-3">Use our API</h3>
                <p className="text-[15px] text-slate-400 font-medium max-w-[280px] leading-relaxed mb-8">
                  Connect your sales tools directly to our API seamlessly.
                </p>
                <button className="bg-white text-slate-900 px-6 py-3 rounded-xl text-[14px] font-bold hover:bg-slate-100 transition-colors shadow-sm">
                  Configure our API's
                </button>
              </div>
              {/* Graphic Bolt */}
              <BoltIcon className="w-64 h-64 text-white/[0.03] absolute -bottom-10 -right-10 transform rotate-12" />
            </div>

            {/* Precision Tip Card */}
            <div className="bg-brand-50 border border-brand-100 rounded-[32px] p-10 relative overflow-hidden shadow-sm">
              <div className="flex items-center gap-2 text-brand-600 mb-4">
                <CheckBadgeIcon className="w-5 h-5" />
                <span className="text-[11px] font-bold uppercase tracking-widest">Precision Tip</span>
              </div>
              <h3 className="text-[24px] font-extrabold text-brand-600 mb-3 tracking-tight">In depth Analysis</h3>
              <p className="text-[15px] text-brand-500/80 font-medium max-w-sm leading-relaxed mb-8">
                Enable "Deep Search" on your next list to find more decision makers for every company you upload.
              </p>
              <button className="bg-brand-500 text-white px-6 py-3 rounded-xl text-[14px] font-bold hover:bg-brand-600 transition-colors shadow-md shadow-brand-500/20">
                Learn More
              </button>
            </div>

          </div>

          {/* Copyright Footer within page flow */}
          <div className="pt-12 pb-6 text-center">
            <p className="text-[11px] font-bold text-slate-400 uppercase tracking-[0.2em]">
              © 2025 Founder Finder. All Rights Reserved
            </p>
          </div>

        </div>
      </div>

      {/* Floating Help Button */}
      <button className="absolute bottom-8 right-8 w-14 h-14 bg-brand-500 hover:bg-brand-600 text-white rounded-2xl flex items-center justify-center shadow-xl shadow-brand-500/30 transition-transform hover:scale-105 active:scale-95 z-50">
        <QuestionMarkCircleIcon className="w-7 h-7" />
      </button>

    </div>
  );
}